import React, { useEffect, useState } from "react";
import api from "../api/client";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Key, Plus, Trash, Copy, Check, WarningCircle } from "@phosphor-icons/react";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const API_BASE = `${BACKEND}/api/public`;
const LITIGATION_URL = `${API_BASE}/litigation-search`;
const BUSINESS_LOOKUP_URL = `${API_BASE}/business-lookup`;

export default function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKey, setNewKey] = useState(null); // full plaintext, shown once
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/api-keys");
      setKeys(data || []);
    } catch { toast.error("Could not load API keys"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const createKey = async (e) => {
    e.preventDefault();
    if (!newKeyName.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/admin/api-keys", { name: newKeyName.trim() });
      setNewKey(data);
      setNewKeyName("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create key");
    } finally { setCreating(false); }
  };

  const revokeKey = async (id, name) => {
    if (!window.confirm(`Revoke "${name}"? Any app using this key will stop working immediately.`)) return;
    try {
      await api.delete(`/admin/api-keys/${id}`);
      toast.success("Key revoked");
      load();
    } catch { toast.error("Failed to revoke"); }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${label}`);
    } catch { toast.error("Copy failed — select and copy manually"); }
  };

  const curlExample = `curl --get "${LITIGATION_URL}" \\
  --data-urlencode "business_name=ACME CORP LLC" \\
  --data-urlencode "business_name=ACME Corp" \\
  --data-urlencode "dba_name=Acme" \\
  --data-urlencode "owner_name=John Smith" \\
  -H "X-API-Key: YOUR_API_KEY_HERE"`;

  const bizCurlExample = `curl --get "${BUSINESS_LOOKUP_URL}" \\
  --data-urlencode "business_name=Twin Tower Construction" \\
  --data-urlencode "ein=12-3456789" \\
  -H "X-API-Key: YOUR_API_KEY_HERE"`;

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-6">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">API Keys</h1>
          <p className="text-slate-600 mt-1">Give external apps programmatic access to Choice Capital&apos;s litigation search.</p>
        </div>
        <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) { setNewKey(null); setNewKeyName(""); } }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="create-api-key-button">
              <Plus size={14} className="mr-1.5" /> Generate new key
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-xl">
            <DialogHeader>
              <DialogTitle>{newKey ? "Save this key now" : "Generate a new API key"}</DialogTitle>
            </DialogHeader>
            {!newKey ? (
              <form onSubmit={createKey} className="space-y-4 py-2">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Label (what will this key be used for?)</label>
                  <Input
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    placeholder="e.g. Internal LMS production"
                    autoFocus
                    data-testid="new-api-key-name"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={creating || !newKeyName.trim()} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="submit-api-key-button">
                    {creating ? "Generating…" : "Generate"}
                  </Button>
                </DialogFooter>
              </form>
            ) : (
              <div className="space-y-4 py-2">
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800 flex gap-2">
                  <WarningCircle size={16} weight="fill" className="shrink-0 mt-0.5" />
                  <div>This is the <strong>only time</strong> you&apos;ll see this key. Copy it now and store it in your other app&apos;s environment variables.</div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Your API key</label>
                  <div className="mt-1 flex gap-2 items-center">
                    <code className="flex-1 px-3 py-2 bg-slate-900 text-emerald-300 rounded-md text-xs font-mono break-all" data-testid="new-api-key-value">{newKey.key}</code>
                    <Button type="button" variant="outline" onClick={() => copy(newKey.key, "key")} data-testid="copy-api-key-button">
                      <Copy size={14} />
                    </Button>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={() => setCreateOpen(false)} className="bg-slate-900 hover:bg-slate-800 text-white">I&apos;ve saved it — close</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Documentation card — LITIGATION SEARCH */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full bg-rose-500"></span>
            <h2 className="font-heading text-xl font-bold tracking-tight">Litigation search</h2>
          </div>
          <p className="text-sm text-slate-500 mt-0.5">Pass your API key in the <code className="bg-slate-100 px-1 rounded text-xs">X-API-Key</code> header. Query by business name, owner name, or both.</p>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">API endpoint URL</label>
          <div className="mt-1 flex gap-2 items-center">
            <code className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-xs font-mono break-all" data-testid="api-endpoint-url">{LITIGATION_URL}</code>
            <Button type="button" variant="outline" size="sm" onClick={() => copy(LITIGATION_URL, "URL")}>
              <Copy size={14} />
            </Button>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Example (curl)</label>
          <div className="mt-1 flex gap-2 items-start">
            <pre className="flex-1 px-3 py-2 bg-slate-900 text-slate-100 rounded-md text-xs font-mono overflow-x-auto whitespace-pre">{curlExample}</pre>
            <Button type="button" variant="outline" size="sm" onClick={() => copy(curlExample, "example")}>
              <Copy size={14} />
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 text-xs text-slate-600 pt-2">
          <div>
            <div className="font-semibold text-slate-900 mb-1">Query parameters</div>
            <ul className="list-disc pl-4 space-y-0.5">
              <li><code>business_name</code> — legal name (repeatable: pass the one from the bank statement AND the one from the application)</li>
              <li><code>dba_name</code> — doing-business-as (repeatable)</li>
              <li><code>owner_name</code> — principal (repeatable)</li>
              <li>At least one required (≥ 2 chars).</li>
            </ul>
          </div>
          <div>
            <div className="font-semibold text-slate-900 mb-1">Response</div>
            <ul className="list-disc pl-4 space-y-0.5">
              <li><code>total_cases</code> — unique cases across all names</li>
              <li><code>cases[]</code> — deduped list (iterate this)</li>
              <li><code>per_query</code> — breakdown by field</li>
              <li><code>deep_links</code> — NY state manual URLs</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Documentation card — BUSINESS LOOKUP (NY) */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full bg-indigo-500"></span>
            <h2 className="font-heading text-xl font-bold tracking-tight">Business start-date lookup (NY)</h2>
          </div>
          <p className="text-sm text-slate-500 mt-0.5">
            Pass a business name — get back the <strong>initial DOS filing date</strong> (when the business was formed in NY), entity type, DOS ID, address, and registered agent.
            Source: NY State Active Corporations dataset. Same <code className="bg-slate-100 px-1 rounded text-xs">X-API-Key</code> header as above.
          </p>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">API endpoint URL</label>
          <div className="mt-1 flex gap-2 items-center">
            <code className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-xs font-mono break-all" data-testid="business-endpoint-url">{BUSINESS_LOOKUP_URL}</code>
            <Button type="button" variant="outline" size="sm" onClick={() => copy(BUSINESS_LOOKUP_URL, "URL")}>
              <Copy size={14} />
            </Button>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Example (curl)</label>
          <div className="mt-1 flex gap-2 items-start">
            <pre className="flex-1 px-3 py-2 bg-slate-900 text-slate-100 rounded-md text-xs font-mono overflow-x-auto whitespace-pre">{bizCurlExample}</pre>
            <Button type="button" variant="outline" size="sm" onClick={() => copy(bizCurlExample, "example")}>
              <Copy size={14} />
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 text-xs text-slate-600 pt-2">
          <div>
            <div className="font-semibold text-slate-900 mb-1">Query parameters</div>
            <ul className="list-disc pl-4 space-y-0.5">
              <li><code>business_name</code> — required (≥ 2 chars). Partial match works.</li>
              <li><code>ein</code> — optional. <strong>Not used to search</strong> (NY DOS doesn&apos;t publish EINs); echoed back for your records.</li>
              <li><code>limit</code> — optional, 1–50 (default 10).</li>
            </ul>
          </div>
          <div>
            <div className="font-semibold text-slate-900 mb-1">Response</div>
            <ul className="list-disc pl-4 space-y-0.5">
              <li><code>best_match.start_date</code> — the date you&apos;re usually after (YYYY-MM-DD)</li>
              <li><code>matches[]</code> — up to <code>limit</code> matches, newest-first</li>
              <li>Each match: <code>entity_name</code>, <code>dos_id</code>, <code>start_date</code>, <code>entity_type</code>, <code>county</code>, <code>address</code>, <code>registered_agent</code></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Key list */}
      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="font-heading text-xl font-bold tracking-tight">Active keys</h2>
          <p className="text-xs text-slate-500 mt-0.5">We only store a hash of each key — the full value is only visible at creation time.</p>
        </div>
        <table className="w-full text-sm" data-testid="api-keys-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.12em] text-slate-500">
            <tr>
              <th className="text-left px-6 py-3 font-semibold">Label</th>
              <th className="text-left px-6 py-3 font-semibold">Prefix</th>
              <th className="text-left px-6 py-3 font-semibold">Created</th>
              <th className="text-left px-6 py-3 font-semibold">Last used</th>
              <th className="text-left px-6 py-3 font-semibold">Calls</th>
              <th className="text-left px-6 py-3 font-semibold">Status</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={7} className="px-6 py-8 text-center text-sm text-slate-500">Loading…</td></tr>
            ) : keys.length === 0 ? (
              <tr><td colSpan={7} className="px-6 py-10 text-center text-sm text-slate-500">
                <Key size={24} weight="duotone" className="mx-auto mb-2 text-slate-400" />
                No API keys yet. Click <strong>Generate new key</strong> to create one.
              </td></tr>
            ) : keys.map((k) => (
              <tr key={k.id} className={k.revoked_at ? "opacity-60" : ""}>
                <td className="px-6 py-3 font-medium text-slate-900">{k.name}</td>
                <td className="px-6 py-3 font-mono text-xs text-slate-600">{k.prefix}…</td>
                <td className="px-6 py-3 text-xs text-slate-500">{new Date(k.created_at).toLocaleString()}</td>
                <td className="px-6 py-3 text-xs text-slate-500">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : <span className="text-slate-400">—</span>}</td>
                <td className="px-6 py-3 text-xs text-slate-600 font-mono">{k.usage_count || 0}</td>
                <td className="px-6 py-3">
                  {k.revoked_at ? (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] font-semibold px-2 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-200">Revoked</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] font-semibold px-2 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200">
                      <Check size={11} weight="bold" /> Active
                    </span>
                  )}
                </td>
                <td className="px-6 py-3 text-right">
                  {!k.revoked_at && (
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-800 hover:bg-red-50" onClick={() => revokeKey(k.id, k.name)} data-testid={`revoke-${k.id}`}>
                      <Trash size={14} />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
